import React from 'react';

import {closestCenter, DndContext, PointerSensor, UniqueIdentifier, useSensor, useSensors} from '@dnd-kit/core';
import {restrictToVerticalAxis} from '@dnd-kit/modifiers';
import {arrayMove, SortableContext, verticalListSortingStrategy} from '@dnd-kit/sortable';

type Props<Item extends {id: UniqueIdentifier} | UniqueIdentifier> = {
	items: Item[];
	onOrderChange: (items: Item[]) => void;
};

function createFindIndexPredicate(id: UniqueIdentifier) {
	return function predicate<Item extends {id: UniqueIdentifier} | UniqueIdentifier>(item: Item) {
		if (typeof item === 'number' || typeof item === 'string') {
			return item === id;
		} else {
			return item.id === id;
		}
	};
}

export function VerticalSortContext<Item extends {id: UniqueIdentifier} | UniqueIdentifier>({
	children,
	items,
	onOrderChange
}: React.PropsWithChildren<Props<Item>>) {
	const sensors = useSensors(useSensor(PointerSensor));
	return (
		<DndContext
			collisionDetection={closestCenter}
			sensors={sensors}
			modifiers={[restrictToVerticalAxis]}
			onDragEnd={({active, over}) => {
				if (over && active.id !== over.id) {
					const oldIndex = items.findIndex(createFindIndexPredicate(active.id));
					const newIndex = items.findIndex(createFindIndexPredicate(over.id));
					onOrderChange(arrayMove(items, oldIndex, newIndex));
				}
			}}>
			<SortableContext items={items} strategy={verticalListSortingStrategy}>
				{children}
			</SortableContext>
		</DndContext>
	);
}
